import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import React, { useState } from 'react';
import { Icon } from 'ui';
import AccordionHeaderToggle, { AccordionItemContentIdContext, getAccordionItemLabelId, getInnerToggleSummaryProps } from './AccordionHeaderToggle';

type AccordionList2HeadingLevel = 2 | 3 | 4 | 5 | 6;

export type AccordionList2Tab = {
  assignment?: { guid?: string };
  children?: React.ReactNode;
  className?: string;
  defaultExpanded?: boolean;
  disabled?: boolean;
  expanded?: boolean;
  hasInnerToggle?: boolean;
  /** Solo con `hasInnerToggle`: envuelve el conmutador en un `<hN>` para que la cabecera cuente en la jerarquía de la página. */
  headingLevel?: AccordionList2HeadingLevel;
  id?: string;
  isHeaderDecorative?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  title?: React.ReactNode;
};

type AccordionList2ItemProps = Readonly<{
  contentId: string;
  headerId: string;
  index: number;
  item: AccordionList2Tab;
  onChange: (item: AccordionList2Tab, expanded: boolean) => void;
  onEntered: (index: number) => void;
  setAccordionRef: (index: number, element: HTMLElement | null) => void;
  shouldScrollIntoViewOnExpand: boolean;
}>;

const HEADING_TAGS: Record<AccordionList2HeadingLevel, 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = { 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' };

type InnerToggleSummaryProps = React.ComponentProps<typeof AccordionSummary> & { summaryId?: string };

// MUI v4 nombra la región del panel con `summary.props.id` y no deja darle otro nombre. Con conmutador interno,
// el contenido de la cabecera mezcla el título (que puede estar oculto a los lectores) con los demás controles:
// el `id` que lee MUI es el del título, y el resumen conserva en el DOM su id de siempre (TAN-10932).
function InnerToggleSummary({ summaryId, ...props }: InnerToggleSummaryProps): JSX.Element {
  return <AccordionSummary {...props} id={summaryId} />;
}

function getSummaryProps(item: AccordionList2Tab, headerId: string, contentId: string): Record<string, unknown> {
  if (item.hasInnerToggle) {
    return { id: getAccordionItemLabelId(contentId), summaryId: headerId, ...getInnerToggleSummaryProps() };
  }
  if (item.isHeaderDecorative) {
    return { 'aria-hidden': true, className: 'accordion-list2__summary--decorative', id: headerId, tabIndex: -1 };
  }
  return { id: headerId };
}

function AccordionList2Item({
  contentId,
  headerId,
  index,
  item,
  onChange,
  onEntered,
  setAccordionRef,
  shouldScrollIntoViewOnExpand,
}: AccordionList2ItemProps): JSX.Element {
  // El conmutador no puede leer el contexto interno de MUI (el bundler resuelve la build ESM y el import directo la CJS);
  // su estado vive aquí para nacer y morir con el panel de MUI, que solo lee `defaultExpanded` al montar
  const [isInnerExpanded, setIsInnerExpanded] = useState<boolean>(() => Boolean(item.defaultExpanded));

  function handleChange(_event: unknown, expanded: boolean): void {
    setIsInnerExpanded(expanded);
    onChange(item, expanded);
  }

  function handleEntered(): void {
    onEntered(index);
  }

  function handleRef(element: HTMLElement | null): void {
    setAccordionRef(index, element);
  }

  const Summary = item.hasInnerToggle ? InnerToggleSummary : AccordionSummary;

  function renderInnerToggle(): JSX.Element {
    const toggle = <AccordionHeaderToggle contentId={contentId} isDisabled={Boolean(item.disabled)} isExpanded={item.expanded ?? isInnerExpanded} />;
    const Heading = item.headingLevel ? HEADING_TAGS[item.headingLevel] : undefined;

    // Solo envuelve el conmutador: el título lleva otros controles («Más opciones», «Ver kanban»…) que no
    // pueden quedar dentro del heading, y el botón ya se nombra con el título (aria-labelledby).
    return Heading ? <Heading className="accordion-list2__heading">{toggle}</Heading> : toggle;
  }

  return (
    <Accordion
      ref={handleRef}
      className={item.className}
      defaultExpanded={item.defaultExpanded}
      disabled={item.disabled}
      expanded={item.expanded}
      id={item.id}
      onChange={handleChange}
      onClick={item.onClick}
      TransitionProps={shouldScrollIntoViewOnExpand ? { onEntered: handleEntered } : undefined}
    >
      <Summary
        expandIcon={item.isHeaderDecorative ? undefined : <Icon name="chevron-down" size="xl" />}
        aria-controls={contentId}
        {...getSummaryProps(item, headerId, contentId)}
      >
        {item.hasInnerToggle && renderInnerToggle()}
        <AccordionItemContentIdContext.Provider value={contentId}>{item.title}</AccordionItemContentIdContext.Provider>
      </Summary>
      <AccordionDetails>{item.children}</AccordionDetails>
    </Accordion>
  );
}

export default AccordionList2Item;
